/* eslint-disable */
import type {
  SRSearchResponse,
  SRDetailsResponse,
  FixVersionsResponse,
  SwTeamsResponse,
  ProductScopesResponse,
  DeliveryDraftResponse,
  DraftEpicInput,
  DraftStoryInput,
  DraftValidationResponse,
  CommitRequest,
  CommitResponse,
  CommitErrorResponse,
  SRDashboardResponse,
  DeliveryTicketsResponse,
  AssigneesResponse,
} from '../types/deliveryDraft';

const API_BASE_URL = '/api/proxy/release-assistant-backend/api';

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  // Avoid "infinite spinner" UX when backend hangs (e.g., Jira calls).
  const timeoutMs = 20_000;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  // If caller provided a signal, forward abort to our controller.
  if (options?.signal) {
    if (options.signal.aborted) {
      controller.abort();
    } else {
      options.signal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError(`Request timed out after ${timeoutMs / 1000}s`, 408, 'Request Timeout');
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new ApiError(
      errorText || `HTTP error! status: ${response.status}`,
      response.status,
      response.statusText
    );
  }

  return response.json();
}

export async function searchSRTickets(query: string, jql?: string): Promise<SRSearchResponse> {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (jql) params.set('jql', jql);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return fetchApi<SRSearchResponse>(`/product/search${suffix}`);
}

export async function getSRDetails(srKey: string): Promise<SRDetailsResponse> {
  return fetchApi<SRDetailsResponse>(`/product/${srKey}`);
}

export async function getFixVersions(projectKey?: string): Promise<FixVersionsResponse> {
  const params = new URLSearchParams();
  if (projectKey) params.set('project_key', projectKey);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return fetchApi<FixVersionsResponse>(`/product/fix-versions${suffix}`);
}

export async function getSwTeams(
  projectKey?: string,
  issueType?: string
): Promise<SwTeamsResponse> {
  const params = new URLSearchParams();
  if (projectKey) params.set('project_key', projectKey);
  if (issueType) params.set('issue_type', issueType);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return fetchApi<SwTeamsResponse>(`/product/sw-teams${suffix}`);
}

export async function getProductScopes(
  projectKey?: string,
  issueType?: string
): Promise<ProductScopesResponse> {
  const params = new URLSearchParams();
  if (projectKey) params.set('project_key', projectKey);
  if (issueType) params.set('issue_type', issueType);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return fetchApi<ProductScopesResponse>(`/product/product-scopes${suffix}`);
}

export async function createDeliveryDraft(payload: {
  sr_key: string;
  epics: DraftEpicInput[];
  standalone_stories: DraftStoryInput[];
  default_fix_version?: string | null;
  product_scope_candidates?: string[];
}): Promise<DeliveryDraftResponse> {
  return fetchApi<DeliveryDraftResponse>('/delivery-drafts', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateDeliveryDraft(
  draftId: string,
  payload: {
    epics: DraftEpicInput[];
    standalone_stories: DraftStoryInput[];
    default_fix_version?: string | null;
  }
): Promise<DeliveryDraftResponse> {
  return fetchApi<DeliveryDraftResponse>(`/delivery-drafts/${draftId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function getDeliveryDraft(draftId: string): Promise<DeliveryDraftResponse> {
  return fetchApi<DeliveryDraftResponse>(`/delivery-drafts/${draftId}`);
}

export async function validateDeliveryDraft(
  draftId: string,
  commitTime: boolean
): Promise<DraftValidationResponse> {
  return fetchApi<DraftValidationResponse>(
    `/delivery-drafts/${draftId}/validate?commit_time=${commitTime ? 'true' : 'false'}`
  );
}

export async function commitDeliveryDraft(
  draftId: string,
  payload: CommitRequest
): Promise<CommitResponse | CommitErrorResponse> {
  return fetchApi<CommitResponse | CommitErrorResponse>(
    `/delivery-drafts/${draftId}/commit`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
}

export async function getSrDashboard(): Promise<SRDashboardResponse> {
  return fetchApi<SRDashboardResponse>('/product/sr-dashboard');
}

export async function getDeliveryTickets(srKey: string): Promise<DeliveryTicketsResponse> {
  return fetchApi<DeliveryTicketsResponse>(`/product/${srKey}/delivery-tickets`);
}

export async function deleteTicket(ticketKey: string): Promise<void> {
  return fetchApi<void>(`/product/tickets/${ticketKey}`, {
    method: 'DELETE',
  });
}

export async function getAssignees(
  query: string,
  projectKey?: string,
  maxResults?: number
): Promise<AssigneesResponse> {
  const params = new URLSearchParams();
  params.set('q', query);
  if (projectKey) params.set('project_key', projectKey);
  if (maxResults) params.set('max_results', maxResults.toString());
  return fetchApi<AssigneesResponse>(`/product/assignees?${params.toString()}`);
}
